import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { microsoftStore, navigation, searchPages, site, webApp } from '../data/site';
import { loadAnalytics, track, usePageViews } from './analytics';
import { useTheme } from './theme';

export function Shell({ children }: { children: React.ReactNode }) {
  const [menuPath, setMenuPath] = useState<string | null>(null);
  const menuButton = useRef<HTMLButtonElement>(null);
  const nav = useRef<HTMLElement>(null);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const home = location.pathname === '/';
  const open = menuPath === location.pathname;
  usePageViews();
  // Analytics load on every marketing page; there is no switch (see `analytics.ts`).
  useEffect(() => {
    loadAnalytics();
  }, []);
  const setOpen = (value: boolean) => setMenuPath(value ? location.pathname : null);
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);
  useEffect(() => {
    const updateHeader = () => setScrolled(window.scrollY > 18);
    updateHeader();
    window.addEventListener('scroll', updateHeader, { passive: true });
    return () => window.removeEventListener('scroll', updateHeader);
  }, []);
  useEffect(() => {
    document.body.classList.toggle('menu-open', open);
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && open) {
        setMenuPath(null);
        menuButton.current?.focus();
      }
      if (event.key === 'Tab' && open) {
        const links = nav.current?.querySelectorAll<HTMLAnchorElement>('a');
        const last = links?.[links.length - 1];
        if (event.shiftKey && document.activeElement === menuButton.current) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          menuButton.current?.focus();
        }
      }
    };
    const desktop = window.matchMedia('(min-width: 1001px)');
    const closeOnDesktop = () => {
      if (desktop.matches) setMenuPath(null);
    };
    desktop.addEventListener('change', closeOnDesktop);
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      document.body.classList.remove('menu-open');
      window.removeEventListener('keydown', closeOnEscape);
      desktop.removeEventListener('change', closeOnDesktop);
    };
  }, [open]);
  useEffect(() => {
    const elements = document.querySelectorAll<HTMLElement>(
      '#main .section-head, #main .card, #main .step, #main .compare, #main .download-panel, #main .store-panel, #main .cta, #main .prose > *, #main .motion-item',
    );
    if (
      !('IntersectionObserver' in window) ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      elements.forEach(element => element.classList.add('is-visible'));
      return;
    }
    // Stagger within each parent rather than across the whole page, so a card's
    // delay reflects its position in its own row instead of its index in the
    // document. Capped at three steps: past that the last card feels late.
    const seen = new Map<Element, number>();
    elements.forEach(element => {
      const parent = element.parentElement;
      const position = parent ? (seen.get(parent) ?? 0) : 0;
      if (parent) seen.set(parent, position + 1);
      element.classList.add('reveal');
      element.style.setProperty('--reveal-delay', `${Math.min(position, 3) * 60}ms`);
    });
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -36px' },
    );
    elements.forEach(element => observer.observe(element));
    return () => observer.disconnect();
  }, [location.pathname]);
  return (
    <div className={`marketing-site${home ? ' home-page' : ''}`}>
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <header className={`site-header${scrolled ? ' scrolled' : ''}`}>
        <div className="container header-inner">
          <Link className="brand" to="/">
            <img
              className="brand-symbol brand-symbol-color"
              src="/brand/logo-symbol-color.png"
              width="48"
              height="48"
              alt=""
            />
            <img
              className="brand-symbol brand-symbol-dark"
              src="/brand/logo-symbol-dark.png"
              width="48"
              height="48"
              alt=""
            />
            <span className="brand-label">{site.name}</span>
          </Link>
          <button
            ref={menuButton}
            className="menu-button"
            aria-label="Toggle navigation"
            aria-expanded={open}
            aria-controls="primary-navigation"
            onClick={() => setOpen(!open)}
          >
            <span />
            <span />
            <span />
          </button>
          <nav
            ref={nav}
            id="primary-navigation"
            className={open ? 'open' : ''}
            aria-label="Primary navigation"
          >
            {navigation.map(item => (
              <NavLink key={item.href} to={item.href} onClick={() => setOpen(false)}>
                {item.label}
              </NavLink>
            ))}
            <Link
              className="button primary"
              to="/start-trial"
              onClick={() => {
                setOpen(false);
                track('trial_cta_click', { placement: 'header' });
              }}
            >
              Start free trial
            </Link>
          </nav>
          <ThemeToggle />
        </div>
      </header>
      {open && (
        <button
          type="button"
          className="menu-backdrop"
          aria-label="Close navigation"
          tabIndex={-1}
          onClick={() => setOpen(false)}
        />
      )}
      <main id="main" tabIndex={-1}>
        {children}
      </main>
      <Footer />
    </div>
  );
}

/**
 * Sun/moon switch. Shows the theme it switches to — the moon in light mode, the
 * sun in dark mode — as a toggle button whose pressed state is "dark theme on".
 * Both icons are rendered and CSS picks one from <html data-theme>, so the
 * prerendered markup is right before hydration.
 */
function ThemeToggle() {
  const { theme, toggle } = useTheme();
  const label = theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme';
  return (
    <button
      type="button"
      className="theme-toggle"
      aria-label="Dark theme"
      aria-pressed={theme === null ? undefined : theme === 'dark'}
      title={theme === null ? undefined : label}
      onClick={() => {
        toggle();
        track('theme_toggle', { theme: theme === 'dark' ? 'light' : 'dark' });
      }}
    >
      <svg className="theme-icon theme-icon-moon" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M20.5 14.2A8.5 8.5 0 0 1 9.8 3.5a8.5 8.5 0 1 0 10.7 10.7Z" />
      </svg>
      <svg className="theme-icon theme-icon-sun" viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2.5v2.2M12 19.3v2.2M4.6 4.6l1.6 1.6M17.8 17.8l1.6 1.6M2.5 12h2.2M19.3 12h2.2M4.6 19.4l1.6-1.6M17.8 6.2l1.6-1.6" />
      </svg>
    </button>
  );
}

function Footer() {
  return (
    <footer>
      <div className="container footer-grid">
        <div className="footer-brand">
          <Link className="brand" to="/">
            <img
              className="brand-symbol"
              src="/brand/logo-symbol-dark.png"
              width="52"
              height="52"
              alt=""
            />
            <span className="brand-label">{site.name}</span>
          </Link>
          <p>{site.description}</p>
        </div>
        <div>
          <strong>Product</strong>
          <Link to="/features">Features</Link>
          <Link to="/desktop">Desktop</Link>
          <Link to="/web">Web app</Link>
          <Link to="/mobile">Mobile</Link>
          <Link to="/integrations">Integrations</Link>
          <Link to="/pricing">Pricing</Link>
        </div>
        <div>
          <strong>Solutions</strong>
          <Link to="/solutions/fleet-operations">Fleet operations</Link>
          <Link to="/solutions/finance-and-reporting">Finance and reporting</Link>
          <Link to="/solutions/fleet-visibility">Fleet visibility</Link>
          <Link to="/solutions/workshop-and-inventory">Workshop and inventory</Link>
        </div>
        <div>
          <strong>Transport software</strong>
          {searchPages.map(page => (
            <Link key={page.slug} to={`/${page.slug}`}>
              {page.label}
            </Link>
          ))}
        </div>
        <div>
          <strong>Downloads</strong>
          <a href={microsoftStore.url} rel="noopener">
            Microsoft Store
          </a>
          <Link to="/downloads/desktop">Desktop apps</Link>
          <a
            href={webApp.url}
            rel="noopener"
            onClick={() => track('open_web_app', { placement: 'footer' })}
          >
            Open the web app
          </a>
          <Link to="/downloads/android">Android</Link>
          <Link to="/downloads/driver">Driver app</Link>
          <Link to="/support">Support</Link>
        </div>
        <div>
          <strong>Company</strong>
          <Link to="/start-trial">Start free trial</Link>
          <Link to="/request-demo">Request a demo</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
          <Link to="/refunds">Refunds</Link>
          <Link to="/delivery">Delivery</Link>
          <Link to="/cookies">Cookies</Link>
          <Link to="/contact">Contact</Link>
        </div>
      </div>
      <div className="container footer-bottom">
        <span>
          © {new Date().getFullYear()} Transport Book · {site.publisher}
        </span>
      </div>
      <div className="footer-wordmark" aria-hidden="true">
        {site.name}
      </div>
    </footer>
  );
}
